import { useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import ButtonV2 from 'components/ButtonV2';
import DateTimePicker from 'components/DateTimePicker';
import AttachmentUpload from 'components/FormFields/AttachmentUpload';
import Input from 'components/FormFields/Input';
import SearchableSelect from 'components/FormFields/SearchableSelect';
import Select from 'components/FormFields/Select';
import {
  PageWrapper,
  Container,
  PageHeader,
  HeaderLeft,
  BackButtonWrapper,
  Title,
  Subtitle,
  FormCard,
  FormSection,
  SectionTitle,
  FormRow,
  FormGroup,
  Label,
  ArrowLeftIcon,
} from 'components/TicketForms';

// GraphQL queries
const GET_ALL_ACCOUNTS = gql`
  query GetAllAccounts {
    getAllAccounts {
      id
      business_dba
    }
  }
`;

const ANNUAL_TYPES = [
  { value: 'membership', label: 'Membership' },
  { value: 'annual', label: 'Annual' },
  { value: 'registration', label: 'Registration' },
];

const CategoryForm = ({
  mode = 'add',
  initialData = null,
  onSubmit,
  onCancel,
  saving = false,
  accountId = null,
}) => {
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    account_id: accountId ? parseInt(accountId) : null,
    type: '',
    code: '',
    code_date: '',
    code_time: '',
    logo_id: null,
    logo: null,
  });
  const [errors, setErrors] = useState({});

  // Fetch accounts
  const { data: accountsData, loading: accountsLoading } =
    useQuery(GET_ALL_ACCOUNTS);

  // Initialize form with existing data when editing
  useEffect(() => {
    if (initialData && mode === 'edit') {
      setFormData({
        name: initialData.name || '',
        account_id: initialData.account?.id || null,
        type: initialData.type || '',
        code: initialData.code || '',
        code_date: initialData.code_date || '',
        code_time: initialData.code_time || '',
        logo_id: initialData.logo_id || null,
        logo: initialData.logo || null,
      });
    }
  }, [initialData, mode]);

  // Pre-select account if accountId is provided
  useEffect(() => {
    if (accountId && !formData.account_id) {
      setFormData((prev) => ({
        ...prev,
        account_id: parseInt(accountId),
      }));
    }
  }, [accountId, formData.account_id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when field is modified
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  // Handler for generic field changes
  const handleFieldChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));
  };

  // Handle logo attachment changes
  const handleLogoChange = (attachments) => {
    if (attachments.length > 0) {
      setFormData((prev) => ({
        ...prev,
        logo_id: attachments[0].id ? String(attachments[0].id) : null,
        logo: attachments[0].url,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        logo_id: null,
        logo: null,
      }));
    }
  };

  // Convert logo to attachments array format for preview
  const getLogoAttachments = () => {
    if (!formData.logo) return [];
    return [
      {
        id: formData.logo_id || 'logo',
        name: 'Category Logo',
        size: 0,
        sizeFormatted: '',
        url: formData.logo,
        type: 'image/*',
        isImage: true,
      },
    ];
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name?.trim()) {
      newErrors.name = 'Category name is required';
    }

    if (!formData.account_id) {
      newErrors.account_id = 'Account is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) {
      toast.error('Please check your form for errors.');
      return;
    }

    try {
      // Clean up the data before submitting
      const submitData = {
        name: formData.name,
        account_id: formData.account_id,
        ...(formData.type && { type: formData.type }),
        ...(formData.code && { code: formData.code }),
        ...(formData.code_date && { code_date: formData.code_date }),
        ...(formData.code_time && { code_time: formData.code_time }),
        ...(formData.logo_id && { logo_id: parseInt(formData.logo_id, 10) }),
      };

      await onSubmit(submitData);
    } catch (error) {
      toast.error(error.message || 'An error occurred');
    }
  };

  // Build account options
  const accountOptions =
    accountsData?.getAllAccounts?.map((acc) => ({
      value: acc.id,
      label: acc.business_dba,
    })) || [];

  const isEdit = mode === 'edit';
  const pageTitle = isEdit ? 'Edit Category' : 'Create Category';
  const pageSubtitle = isEdit
    ? 'Update the category details below'
    : 'Fill in the details to create a new annual/season category';

  return (
    <PageWrapper>
      <Container>
        {/* Header */}
        <PageHeader>
          <HeaderLeft>
            <BackButtonWrapper>
              <ButtonV2 onClick={onCancel} icon={<ArrowLeftIcon />}>
                Back to Categories
              </ButtonV2>
            </BackButtonWrapper>
            <Title>{pageTitle}</Title>
            <Subtitle>{pageSubtitle}</Subtitle>
          </HeaderLeft>
        </PageHeader>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <FormCard>
            <FormSection>
              <SectionTitle>Basic Information</SectionTitle>

              <FormRow columns="1fr 1fr">
                <FormGroup>
                  <SearchableSelect
                    label="Account"
                    placeholder="Select an account"
                    options={accountOptions}
                    value={formData.account_id}
                    onChange={(value) =>
                      handleSelectChange('account_id', value)
                    }
                    error={errors.account_id}
                    loading={accountsLoading}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Input
                    label="Category Name"
                    name="name"
                    placeholder="Enter category name"
                    value={formData.name}
                    onChange={handleInputChange}
                    error={errors.name}
                    required
                  />
                </FormGroup>
              </FormRow>

              <FormRow columns="1fr 1fr">
                <FormGroup>
                  <Select
                    label="Category Type"
                    placeholder="Select type"
                    options={ANNUAL_TYPES}
                    value={formData.type}
                    onChange={(e) => handleSelectChange('type', e.target.value)}
                  />
                </FormGroup>

                <FormGroup>
                  <Input
                    label="Access Code"
                    name="code"
                    placeholder="Enter access code (optional)"
                    value={formData.code}
                    onChange={handleInputChange}
                  />
                </FormGroup>
              </FormRow>
            </FormSection>

            <FormSection>
              <SectionTitle>Category Logo</SectionTitle>
              <FormRow>
                <FormGroup>
                  <Label>Logo Image</Label>
                  <AttachmentUpload
                    attachments={getLogoAttachments()}
                    onAttachmentsChange={handleLogoChange}
                    maxFiles={1}
                    maxFileSize={5242880}
                    channel="SMS"
                  />
                </FormGroup>
              </FormRow>
            </FormSection>

            <FormSection>
              <SectionTitle>Access Code Expiration</SectionTitle>
              <Subtitle style={{ marginTop: '-8px', marginBottom: '16px' }}>
                Set when the access code should expire (optional)
              </Subtitle>

              <FormRow columns="1fr 1fr">
                <FormGroup>
                  <Label>End Date</Label>
                  <DateTimePicker
                    value={formData.code_date}
                    onChange={(value) => handleFieldChange('code_date', value)}
                    showTime={false}
                  />
                </FormGroup>

                <FormGroup>
                  <Label>End Time</Label>
                  <DateTimePicker
                    value={formData.code_time}
                    onChange={(value) => handleFieldChange('code_time', value)}
                    timeOnly
                  />
                </FormGroup>
              </FormRow>
            </FormSection>

            {/* Action Buttons */}
            <FormSection
              style={{
                borderTop: '1px solid rgba(255,255,255,0.1)',
                paddingTop: '24px',
                marginTop: '8px',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                }}
              >
                <ButtonV2 type="button" onClick={onCancel} disabled={saving}>
                  Cancel
                </ButtonV2>
                <ButtonV2
                  type="submit"
                  primary
                  loading={saving}
                  disabled={saving}
                >
                  {isEdit ? 'Update Category' : 'Create Category'}
                </ButtonV2>
              </div>
            </FormSection>
          </FormCard>
        </form>
      </Container>
    </PageWrapper>
  );
};

export default CategoryForm;
